import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { getAllOrganizationNames } from '../../Services/OrganizationService';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import AddressSelectorFields from '../Common/AddressSelectorFields';
import { getAllProviderSettings, saveProvider } from '../../Services/ProviderService';
import { GetCountries, GetState } from 'react-country-state-city';
import CustomDatePicker from '../Common/CustomDatePicker';
import CustomPhoneInput from '../Common/CustomPhoneInput';

const SETTING_TYPE_IDS = {
  prefix: 18,
  suffix: 19,
  provider_title: 20,
  gender: 29,
  status: 51,
  telemed_exp: 50,
};

const parseNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

// The provider's own id (and any id resolved from it) is a UUID string, not a
// number — Provider.id is a UUID column on the backend. Number("<uuid>") is
// always NaN, so parseNumberOrNull() must never be used on it or every save
// silently loses the id and goes out as a create.
const parseIdOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

const emptyForm = {
  id: null,
  type: 'create',
  prefix: null,
  first_name: '',
  middle_name: '',
  last_name: '',
  suffix: null,
  provider_title: null,
  telemed_exp: null,
  personal_email: '',
  work_email: '',
  home_phone: '',
  mobile_phone: '',
  address_id: null,
  address: {
    street: '',
    street2: '',
    city: '',
    state: '',
    country: 'United States',
    zipcode: '',
  },
  birth_address_id: null,
  birth_address: {
    street: '',
    street2: '',
    city: '',
    state: '',
    country: 'United States',
    zipcode: '',
  },
  primary_location_address_id: null,
  primary_location_address: {
    street: '',
    street2: '',
    city: '',
    state: '',
    country: 'United States',
    zipcode: '',
  },
  secondary_location_address_id: null,
  secondary_location_address: {
    street: '',
    street2: '',
    city: '',
    state: '',
    country: 'United States',
    zipcode: '',
  },
  gender: null,
  date_of_birth: null,
  incentive_percentage: null,
  is_authorized_to_work: false,
  caqh_id: '',
  npi_number: '',
  license_id: '',
  license_state: '',
  license_issue_date: null,
  license_exp_date: null,
  npi_enum_date: null,
  npi_social_security: '',
  caqh_issue_date: null,
  caqh_attention_date: null,
  caqh_reattestation_date: null,
  caqh_enabled: false,
  organization_id: '',
  status_id: null,
};

const validationSchema = Yup.object({
  type: Yup.string().nullable(),
  first_name: Yup.string().trim().required('First name is required'),
  last_name: Yup.string().trim().required('Last name is required'),
  middle_name: Yup.string().trim().nullable(),
  prefix: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .nullable(),
  suffix: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .nullable(),
  provider_title: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .nullable(),
  personal_email: Yup.string().email('Invalid email format').trim().nullable(),
  work_email: Yup.string().email('Invalid email format').trim().nullable(),
  home_phone: Yup.string().trim().nullable(),
  mobile_phone: Yup.string().trim().nullable(),
  gender: Yup.string().nullable(),
  date_of_birth: Yup.date().nullable().typeError('Invalid date format'),
  incentive_percentage: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .typeError('Incentive percentage must be a number')
    .min(0, 'Must be at least 0')
    .max(100, 'Must be at most 100')
    .nullable(),
  is_authorized_to_work: Yup.boolean().nullable(),
  caqh_id: Yup.string().trim().nullable(),
  npi_number: Yup.string().trim().matches(/^\d{10}$/, 'NPI must be 10 digits').nullable(),
  license_id: Yup.string().trim().nullable(),
  license_state: Yup.string().trim().nullable(),
  license_issue_date: Yup.date().nullable().typeError('Invalid date format'),
  license_exp_date: Yup.date().nullable().typeError('Invalid date format'),
  npi_enum_date: Yup.date().nullable().typeError('Invalid date format'),
  npi_social_security: Yup.string().trim().nullable(),
  caqh_issue_date: Yup.date().nullable().typeError('Invalid date format'),
  caqh_attention_date: Yup.date().nullable().typeError('Invalid date format'),
  caqh_reattestation_date: Yup.date().nullable().typeError('Invalid date format'),
  caqh_enabled: Yup.boolean().nullable(),
  telemed_exp: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .nullable(),
  address_id: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .nullable(),
  address: Yup.object({
    street: Yup.string().trim().nullable(),
    street2: Yup.string().trim().nullable(),
    city: Yup.string().trim().nullable(),
    state: Yup.string().trim().nullable(),
    country: Yup.string().trim().nullable(),
    zipcode: Yup.string().trim().nullable(),
  }).nullable(),
  birth_address_id: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .nullable(),
  birth_address: Yup.object({
    street: Yup.string().trim().nullable(),
    street2: Yup.string().trim().nullable(),
    city: Yup.string().trim().nullable(),
    state: Yup.string().trim().nullable(),
    country: Yup.string().trim().nullable(),
    zipcode: Yup.string().trim().nullable(),
  }).nullable(),
  primary_location_address_id: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .nullable(),
  primary_location_address: Yup.object({
    street: Yup.string().trim().nullable(),
    street2: Yup.string().trim().nullable(),
    city: Yup.string().trim().nullable(),
    state: Yup.string().trim().nullable(),
    country: Yup.string().trim().nullable(),
    zipcode: Yup.string().trim().nullable(),
  }).nullable(),
  secondary_location_address_id: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .nullable(),
  secondary_location_address: Yup.object({
    street: Yup.string().trim().nullable(),
    street2: Yup.string().trim().nullable(),
    city: Yup.string().trim().nullable(),
    state: Yup.string().trim().nullable(),
    country: Yup.string().trim().nullable(),
    zipcode: Yup.string().trim().nullable(),
  }).nullable(),
  organization_id: Yup.string().trim().required('Organization is required'),
  status_id: Yup.number()
    .transform((value, originalValue) => parseNumberOrNull(originalValue))
    .nullable()
    .required('Status is required'),
});

function mapApiToForm(providerData, fallbackProviderId) {
  const effectiveId = parseIdOrNull(providerData?.id ?? fallbackProviderId);

  return {
    id: effectiveId,
    type: effectiveId ? 'update' : (providerData?.type ?? 'create'),
    prefix: parseNumberOrNull(providerData?.prefix),
    first_name: providerData?.first_name ?? providerData?.firstName ?? '',
    middle_name: providerData?.middle_name ?? providerData?.middleName ?? '',
    last_name: providerData?.last_name ?? providerData?.lastName ?? '',
    suffix: parseNumberOrNull(providerData?.suffix),
    provider_title: parseNumberOrNull(providerData?.provider_title ?? providerData?.providerTitle),
    telemed_exp: parseNumberOrNull(providerData?.telemed_exp ?? providerData?.telemedExp),
    personal_email: providerData?.personal_email ?? providerData?.personalEmail ?? '',
    work_email: providerData?.work_email ?? providerData?.workEmail ?? '',
    home_phone: providerData?.home_phone ?? providerData?.homePhone ?? '',
    mobile_phone: providerData?.mobile_phone ?? providerData?.mobilePhone ?? '',
    address_id: parseNumberOrNull(providerData?.address_id ?? providerData?.addressId),
    address: {
      street:
        providerData?.address?.street ??
        providerData?.address?.Street ??
        providerData?.address?.address_1 ??
        providerData?.address?.address1 ??
        providerData?.street ??
        providerData?.Street ??
        '',
      street2:
        providerData?.address?.street2 ??
        providerData?.address?.Street2 ??
        providerData?.address?.address_2 ??
        providerData?.address?.address2 ??
        providerData?.street2 ??
        providerData?.Street2 ??
        '',
      city:
        providerData?.address?.city ??
        providerData?.address?.City ??
        providerData?.city ??
        providerData?.City ??
        '',
      state:
        providerData?.address?.state ??
        providerData?.address?.State ??
        providerData?.state ??
        providerData?.State ??
        '',
      country:
        providerData?.address?.country ??
        providerData?.address?.Country ??
        providerData?.country ??
        providerData?.Country ??
        'United States',
      zipcode:
        providerData?.address?.zipcode ??
        providerData?.address?.Zipcode ??
        providerData?.address?.zip_code ??
        providerData?.address?.zipCode ??
        providerData?.zipcode ??
        providerData?.Zipcode ??
        '',
    },
    birth_address_id: parseNumberOrNull(providerData?.birth_address_id ?? providerData?.birthAddressId),
    birth_address: {
      street:
        providerData?.birth_address?.street ??
        providerData?.birth_address?.Street ??
        providerData?.birthStreet ??
        providerData?.BirthStreet ??
        '',
      street2:
        providerData?.birth_address?.street2 ??
        providerData?.birth_address?.Street2 ??
        providerData?.birthStreet2 ??
        providerData?.BirthStreet2 ??
        '',
      city:
        providerData?.birth_address?.city ??
        providerData?.birth_address?.City ??
        providerData?.birthCity ??
        providerData?.BirthCity ??
        '',
      state:
        providerData?.birth_address?.state ??
        providerData?.birth_address?.State ??
        providerData?.birthState ??
        providerData?.BirthState ??
        '',
      country:
        providerData?.birth_address?.country ??
        providerData?.birth_address?.Country ??
        providerData?.birthCountry ??
        providerData?.BirthCountry ??
        'United States',
      zipcode:
        providerData?.birth_address?.zipcode ??
        providerData?.birth_address?.Zipcode ??
        providerData?.birthZipcode ??
        providerData?.BirthZipcode ??
        '',
    },
    primary_location_address_id: parseNumberOrNull(
      providerData?.primary_location_address_id ?? providerData?.primaryLocationAddressId
    ),
    primary_location_address: {
      street:
        providerData?.primary_location_address?.street ??
        providerData?.primary_location_address?.Street ??
        providerData?.primary_location_address?.address_1 ??
        providerData?.primary_location_address?.address1 ??
        providerData?.primaryLocationAddress?.street ??
        providerData?.primaryLocationAddress?.Street ??
        providerData?.primaryLocationStreet ??
        providerData?.PrimaryLocationStreet ??
        '',
      street2:
        providerData?.primary_location_address?.street2 ??
        providerData?.primary_location_address?.Street2 ??
        providerData?.primary_location_address?.address_2 ??
        providerData?.primary_location_address?.address2 ??
        providerData?.primaryLocationAddress?.street2 ??
        providerData?.primaryLocationAddress?.Street2 ??
        providerData?.primaryLocationStreet2 ??
        providerData?.PrimaryLocationStreet2 ??
        '',
      city:
        providerData?.primary_location_address?.city ??
        providerData?.primary_location_address?.City ??
        providerData?.primary_location_address?.city ??
        providerData?.primaryLocationAddress?.city ??
        providerData?.primaryLocationAddress?.City ??
        providerData?.primaryLocationCity ??
        providerData?.PrimaryLocationCity ??
        '',
      state:
        providerData?.primary_location_address?.state ??
        providerData?.primary_location_address?.State ??
        providerData?.primaryLocationAddress?.state ??
        providerData?.primaryLocationAddress?.State ??
        providerData?.primaryLocationState ??
        providerData?.PrimaryLocationState ??
        '',
      country:
        providerData?.primary_location_address?.country ??
        providerData?.primary_location_address?.Country ??
        providerData?.primaryLocationAddress?.country ??
        providerData?.primaryLocationAddress?.Country ??
        providerData?.primaryLocationCountry ??
        providerData?.PrimaryLocationCountry ??
        'United States',
      zipcode:
        providerData?.primary_location_address?.zipcode ??
        providerData?.primary_location_address?.Zipcode ??
        providerData?.primary_location_address?.zip_code ??
        providerData?.primary_location_address?.zipCode ??
        providerData?.primaryLocationAddress?.zipcode ??
        providerData?.primaryLocationAddress?.Zipcode ??
        providerData?.primaryLocationZipcode ??
        providerData?.PrimaryLocationZipcode ??
        '',
    },
    secondary_location_address_id: parseNumberOrNull(
      providerData?.secondary_location_address_id ?? providerData?.secondaryLocationAddressId
    ),
    secondary_location_address: {
      street:
        providerData?.secondary_location_address?.street ??
        providerData?.secondary_location_address?.Street ??
        providerData?.secondary_location_address?.address_1 ??
        providerData?.secondary_location_address?.address1 ??
        providerData?.secondaryLocationAddress?.street ??
        providerData?.secondaryLocationAddress?.Street ??
        providerData?.secondaryLocationStreet ??
        providerData?.SecondaryLocationStreet ??
        '',
      street2:
        providerData?.secondary_location_address?.street2 ??
        providerData?.secondary_location_address?.Street2 ??
        providerData?.secondary_location_address?.address_2 ??
        providerData?.secondary_location_address?.address2 ??
        providerData?.secondaryLocationAddress?.street2 ??
        providerData?.secondaryLocationAddress?.Street2 ??
        providerData?.secondaryLocationStreet2 ??
        providerData?.SecondaryLocationStreet2 ??
        '',
      city:
        providerData?.secondary_location_address?.city ??
        providerData?.secondary_location_address?.City ??
        providerData?.secondaryLocationAddress?.city ??
        providerData?.secondaryLocationAddress?.City ??
        providerData?.secondaryLocationCity ??
        providerData?.SecondaryLocationCity ??
        '',
      state:
        providerData?.secondary_location_address?.state ??
        providerData?.secondary_location_address?.State ??
        providerData?.secondaryLocationAddress?.state ??
        providerData?.secondaryLocationAddress?.State ??
        providerData?.secondaryLocationState ??
        providerData?.SecondaryLocationState ??
        '',
      country:
        providerData?.secondary_location_address?.country ??
        providerData?.secondary_location_address?.Country ??
        providerData?.secondaryLocationAddress?.country ??
        providerData?.secondaryLocationAddress?.Country ??
        providerData?.secondaryLocationCountry ??
        providerData?.SecondaryLocationCountry ??
        'United States',
      zipcode:
        providerData?.secondary_location_address?.zipcode ??
        providerData?.secondary_location_address?.Zipcode ??
        providerData?.secondary_location_address?.zip_code ??
        providerData?.secondary_location_address?.zipCode ??
        providerData?.secondaryLocationAddress?.zipcode ??
        providerData?.secondaryLocationAddress?.Zipcode ??
        providerData?.secondaryLocationZipcode ??
        providerData?.SecondaryLocationZipcode ??
        '',
    },
    gender: providerData?.gender != null && providerData?.gender !== '' ? String(providerData.gender) : null,
    date_of_birth: providerData?.date_of_birth || providerData?.dateOfBirth || null,
    incentive_percentage: parseNumberOrNull(
      providerData?.incentive_percentage ?? providerData?.incentivePercentage
    ),
    is_authorized_to_work: providerData?.is_authorized_to_work ?? providerData?.isAuthorizedToWork ?? false,
    caqh_id: providerData?.caqh_id ?? providerData?.caqhId ?? providerData?.cahq_id ?? providerData?.cahqId ?? '',
    npi_number: providerData?.npi_number ?? providerData?.npiNumber ?? '',
    license_id: providerData?.license_id ?? providerData?.licenseId ?? '',
    license_state: providerData?.license_state ?? providerData?.licenseState ?? '',
    license_issue_date: providerData?.license_issue_date || providerData?.licenseIssueDate || null,
    license_exp_date: providerData?.license_exp_date || providerData?.licenseExpDate || null,
    npi_enum_date: providerData?.npi_enum_date || providerData?.npiEnumDate || null,
    npi_social_security: providerData?.npi_social_security ?? providerData?.npiSocialSecurity ?? '',
    caqh_issue_date: providerData?.caqh_issue_date || providerData?.caqhIssueDate || null,
    caqh_attention_date: providerData?.caqh_attention_date || providerData?.caqhAttentionDate || null,
    caqh_reattestation_date: providerData?.caqh_reattestation_date || providerData?.caqhReattestationDate || null,
    caqh_enabled: providerData?.caqh_enabled ?? providerData?.caqhEnabled ?? false,
    organization_id: providerData?.organization_id ?? providerData?.organizationId ?? '',
    status_id: parseNumberOrNull(providerData?.status_id ?? providerData?.statusId),
  };
}

export default function PersonalInfo({ initialData, providerId, onSaveSuccess, onContinue }) {
  const { token } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);

  const PROVIDER_ID_STORAGE_KEY = 'providerForm.providerId';
  const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

  const parseReturnedId = (res) => {
    if (!res) return null;
    if (typeof res === 'number' || typeof res === 'string') {
      return String(res);
    }
    const d = res?.data ?? res;
    const candidate =
      d?.id ??
      d?.Id ??
      d?.provider_id ??
      d?.ProviderId ??
      (typeof d === 'number' || typeof d === 'string' ? d : null) ??
      res?.id ??
      res?.Id ??
      res?.provider_id ??
      res?.ProviderId ??
      null;
    return candidate != null ? String(candidate) : null;
  };

  const initialFormValues = useMemo(() => {
    const storedId = typeof localStorage !== 'undefined' ? localStorage.getItem(PROVIDER_ID_STORAGE_KEY) : null;
    const effectiveId = parseIdOrNull(
      initialData?.id ??
      initialData?.Id ??
      initialData?.provider_id ??
      initialData?.ProviderId ??
      initialData?.providerId ??
      providerId ??
      storedId
    );

    if (initialData) {
      const mapped = mapApiToForm(initialData, effectiveId);
      return {
        ...mapped,
        id: effectiveId || mapped.id || null,
        type: (effectiveId || mapped.id) ? 'update' : 'create',
      };
    }
    if (effectiveId) {
      return {
        ...emptyForm,
        id: effectiveId,
        type: 'update',
      };
    }
    return emptyForm;
  }, [initialData, providerId]);

  const persistProviderIds = (providerIdValue, organizationIdValue) => {
    try {
      if (providerIdValue != null && providerIdValue !== '') {
        localStorage.setItem(PROVIDER_ID_STORAGE_KEY, String(providerIdValue));
      }
      if (organizationIdValue != null && organizationIdValue !== '') {
        localStorage.setItem(ORGANIZATION_ID_STORAGE_KEY, String(organizationIdValue));
      }
    } catch (storageError) {
      console.error('Failed to save provider/organization id to localStorage:', storageError);
    }
  };

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        const storedId = typeof localStorage !== 'undefined' ? localStorage.getItem(PROVIDER_ID_STORAGE_KEY) : null;
        const effectiveProviderId = parseIdOrNull(
          values.id ??
          values.Id ??
          values.provider_id ??
          values.ProviderId ??
          initialData?.id ??
          initialData?.Id ??
          initialData?.provider_id ??
          initialData?.ProviderId ??
          initialData?.providerId ??
          providerId ??
          storedId
        );

        const payload = {
          ...values,
          id: effectiveProviderId || null,
          type: effectiveProviderId ? 'update' : 'create',
        };

        // Normalize non-strings:
        // 1. Date fields -> null if empty
        const dateFields = [
          'date_of_birth',
          'license_issue_date',
          'license_exp_date',
          'caqh_issue_date',
          'caqh_attention_date',
          'caqh_reattestation_date',
          'npi_enum_date',
        ];
        dateFields.forEach((f) => {
          if (payload[f] === '' || payload[f] === undefined || payload[f] === null) {
            payload[f] = null;
          }
        });

        // 2. Number / Setting ID fields -> null if empty, otherwise Number.
        // 'id' is deliberately NOT in this list: Provider.id is a UUID on the
        // backend, and Number("<uuid>") is NaN, which would wipe it back to
        // null right after effectiveProviderId resolved it correctly above.
        const intFields = [
          'prefix',
          'suffix',
          'provider_title',
          'telemed_exp',
          'status_id',
          'address_id',
          'birth_address_id',
          'primary_location_address_id',
          'secondary_location_address_id',
        ];
        intFields.forEach((f) => {
          if (payload[f] === '' || payload[f] === undefined || payload[f] === null) {
            payload[f] = null;
          } else {
            const num = Number(payload[f]);
            payload[f] = Number.isNaN(num) ? null : num;
          }
        });

        // 3. Gender -> string or null
        if (payload.gender === '' || payload.gender === undefined || payload.gender === null) {
          payload.gender = null;
        } else {
          payload.gender = String(payload.gender);
        }

        // 3. Float/Decimal fields -> null if empty, otherwise Number
        if (
          payload.incentive_percentage === '' ||
          payload.incentive_percentage === undefined ||
          payload.incentive_percentage === null
        ) {
          payload.incentive_percentage = null;
        } else {
          const num = Number(payload.incentive_percentage);
          payload.incentive_percentage = Number.isNaN(num) ? null : num;
        }

        // 4. Boolean fields
        payload.is_authorized_to_work = Boolean(payload.is_authorized_to_work);
        payload.caqh_enabled = Boolean(payload.caqh_enabled);

        const response = await saveProvider(token, payload);
        if (response?.is_success) {
          const providerSavedId = parseReturnedId(response) || effectiveProviderId || payload.id || null;
          const organizationSavedId = values.organization_id ?? values.organizationId ?? null;

          if (providerSavedId) {
            persistProviderIds(providerSavedId, organizationSavedId);
            formik.setFieldValue('id', providerSavedId);
            formik.setFieldValue('type', 'update');
          }

          if (onSaveSuccess) {
            onSaveSuccess(providerSavedId || response);
          }

          if (onContinue) {
            onContinue(
              {
                ...values,
                id: providerSavedId || values.id,
                type: 'update',
              },
              providerSavedId || null
            );
          }
        } else if(response?.status_code === 500 && !response?.is_success) {
          toast.error('Internal Server Error. Please Contact Support Team.');
        } else{
          toast.error(response?.message || 'Failed to save personal information');
        }
      } catch (error) {
        console.error(error);
        toast.error(error.message || 'Failed to save personal information');
      }
    },
  });

  const normalizeArray = (value) => (Array.isArray(value) ? value : []);

  const [providerSettings, setProviderSettings] = useState({
    prefix: [],
    suffix: [],
    provider_title: [],
    gender: [],
    telemed_exp: [],
    status: [],
  });
  const [licenseStateOptions, setLicenseStateOptions] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const mapProviderSettingItem = (item) => ({
    value: item.id ?? item.Id ?? item.id,
    label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
  });

  const loadProviderSettings = async () => {
    try {
      setSettingsLoading(true);
      const entries = Object.entries(SETTING_TYPE_IDS);
      const results = await Promise.all(
        entries.map(async ([key, typeId]) => {
          try {
            const response = await getAllProviderSettings(token, typeId);
            if (response?.is_success) {
              return {
                key,
                options: normalizeArray(response?.data).map(mapProviderSettingItem),
              };
            } else if (response?.is_success === false && response?.status_code === 500) {
              toast.error('Internal server error. Please contact support team.');
            } else {
              // toast.error(response?.message || `Failed to load settings`);
              console.error(`Failed to load ${key} settings:`, response?.message || 'Unknown error');
            }
          } catch (error) {
            console.error(`Failed to load provider settings for ${key}:`, error);
          }
          return { key, options: [] };
        })
      );

      setProviderSettings(results.reduce((acc, item) => ({ ...acc, [item.key]: item.options }), {}));
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadLicenseStateOptions = async () => {
    try {
      const countries = await GetCountries();
      const unitedStates = Array.isArray(countries)
        ? countries.find((country) =>
          ['united states', 'us', 'usa'].includes(String(country.name ?? '').trim().toLowerCase()) ||
          String(country.iso2 ?? '').toUpperCase() === 'US' ||
          String(country.iso3 ?? '').toUpperCase() === 'USA'
        )
        : null;
      const countryId = unitedStates?.id ?? unitedStates?.iso2 ?? unitedStates?.iso3 ?? 'US';
      const stateList = await GetState(countryId);
      if (Array.isArray(stateList)) {
        setLicenseStateOptions(
          stateList.map((state) => ({
            value: state.name ?? state.state_code ?? state.code ?? '',
            label: state.name ?? state.state_code ?? state.code ?? '',
            key: state.id ?? state.state_code ?? state.name,
          }))
        );
      }
    } catch (error) {
      console.error('Failed to load license state options:', error);
      setLicenseStateOptions([]);
    }
  };

  const loadOrganizations = async () => {
    try {
      setOrgLoading(true);
      const response = await getAllOrganizationNames(token);
      if (response?.is_success) {
        const data = normalizeArray(response?.data?.Organizations ?? response?.data ?? response);
        setOrganizations(data);
      } else if (response?.is_success === false && response?.status_code === 500) {
        setOrganizations([]);
        toast.error('Internal server error. Please contact support team.');
      } else {
        setOrganizations([]);
        // toast.error(response?.message || 'Failed to load organizations');
        console.error('Failed to load organizations:', response?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
      setOrganizations([]);
    } finally {
      setOrgLoading(false);
    }
  };

  React.useEffect(() => {
    if (!token) return;
    loadOrganizations();
    loadProviderSettings();
    loadLicenseStateOptions();
  }, [token]);

  const renderAsterisk = () => <span className="text-danger">*</span>;

  const getFieldError = (fieldName) => {
    return formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : '';
  };

  return (
    <div className="container-fluid p-0">
      <form onSubmit={formik.handleSubmit}>
        {/* Personal Information Section */}
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">Personal Information</h6>
          <div className="row g-3">
            <div className="col-12 col-md-3 col-lg-2">
              <SearchableDropdown
                label="Prefix"
                placeholder="Select Option"
                options={providerSettings.prefix}
                value={formik.values.prefix ?? ''}
                onChange={(value) => formik.setFieldValue('prefix', parseNumberOrNull(value))}
                disabled={settingsLoading}
                loading={settingsLoading}
                className={getFieldError('prefix') ? 'is-invalid' : ''}
                emptyMessage="No prefixes found"
              />
              <div className="invalid-feedback d-block">{getFieldError('prefix')}</div>
            </div>

            <div className="col-12 col-md-4 col-lg-3">
              <label className="form-label fw-semibold">
                First Name {renderAsterisk()}
              </label>
              <input
                name="first_name"
                placeholder="Enter first name"
                className={`form-control rounded-3 shadow-sm ${getFieldError('first_name') ? 'is-invalid' : ''}`}
                value={formik.values.first_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="invalid-feedback d-block">{getFieldError('first_name')}</div>
            </div>

            <div className="col-12 col-md-4 col-lg-3 ">
              <label className="form-label fw-semibold">Middle Name</label>
              <input
                name="middle_name"
                placeholder="Enter middle name"
                className={`form-control rounded-3 shadow-sm ${getFieldError('middle_name') ? 'is-invalid' : ''}`}
                value={formik.values.middle_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="invalid-feedback d-block">{getFieldError('middle_name')}</div>
            </div>

            <div className="col-12 col-md-4 col-lg-3">
              <label className="form-label fw-semibold">
                Last Name {renderAsterisk()}
              </label>
              <input
                name="last_name"
                placeholder="Enter last name"
                className={`form-control rounded-3 shadow-sm ${getFieldError('last_name') ? 'is-invalid' : ''}`}
                value={formik.values.last_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="invalid-feedback d-block">{getFieldError('last_name')}</div>
            </div>

            <div className="col-12 col-md-3 col-lg-1">
              <SearchableDropdown
                label="Suffix"
                placeholder="Select Option"
                options={providerSettings.suffix}
                value={formik.values.suffix ?? ''}
                onChange={(value) => formik.setFieldValue('suffix', parseNumberOrNull(value))}
                disabled={settingsLoading}
                loading={settingsLoading}
                className={getFieldError('suffix') ? 'is-invalid' : ''}
                emptyMessage="No suffixes found"
              />
              <div className="invalid-feedback d-block">{getFieldError('suffix')}</div>
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12 col-md-8">
              <SearchableDropdown
                label="Provider Title"
                placeholder="Select Option"
                options={providerSettings.provider_title}
                value={formik.values.provider_title ?? ''}
                onChange={(value) => formik.setFieldValue('provider_title', parseNumberOrNull(value))}
                disabled={settingsLoading}
                loading={settingsLoading}
                className={getFieldError('provider_title') ? 'is-invalid' : ''}
                emptyMessage="No titles found"
              />
              <div className="invalid-feedback d-block">{getFieldError('provider_title')}</div>
            </div>

            <div className="col-12 col-md-4">
              <SearchableDropdown
                label="Gender"
                placeholder="Select Option"
                options={providerSettings.gender}
                value={formik.values.gender ?? ''}
                onChange={(value) =>
                  formik.setFieldValue('gender', value !== '' && value !== null && value !== undefined ? String(value) : null)
                }
                disabled={settingsLoading}
                loading={settingsLoading}
                className={getFieldError('gender') ? 'is-invalid' : ''}
                emptyMessage="No genders found"
              />
              <div className="invalid-feedback d-block">{getFieldError('gender')}</div>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">Date of Birth</label>

              <CustomDatePicker
                name="date_of_birth"
                className={`form-control rounded-3 shadow-sm ${getFieldError('date_of_birth') ? 'is-invalid' : ''}`}
                value={formik.values.date_of_birth}
                formik={formik}
              />

              {/* <input
                name="date_of_birth"
                type="date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('date_of_birth') ? 'is-invalid' : ''}`}
                value={formik.values.date_of_birth}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              /> */}


              <div className="invalid-feedback d-block">{getFieldError('date_of_birth')}</div>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">Incentive Percentage (%)</label>
              <input
                name="incentive_percentage"
                type="number"
                placeholder="e.g. 45"
                step="0.001"
                min="0"
                max="100"
                className={`form-control rounded-3 shadow-sm ${getFieldError('incentive_percentage') ? 'is-invalid' : ''}`}
                value={formik.values.incentive_percentage}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="invalid-feedback d-block">{getFieldError('incentive_percentage')}</div>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">Contact Information</h6>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">Personal Email</label>
              <input
                name="personal_email"
                type="email"
                placeholder="personal@example.com"
                className={`form-control rounded-3 shadow-sm ${getFieldError('personal_email') ? 'is-invalid' : ''}`}
                value={formik.values.personal_email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="invalid-feedback d-block">{getFieldError('personal_email')}</div>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">Work Email</label>
              <input
                name="work_email"
                type="email"
                placeholder="work@example.com"
                className={`form-control rounded-3 shadow-sm ${getFieldError('work_email') ? 'is-invalid' : ''}`}
                value={formik.values.work_email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="invalid-feedback d-block">{getFieldError('work_email')}</div>
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">Home Phone</label>

                <CustomPhoneInput
                formik={formik}
                name="home_phone"
                placeholder="+1 (555) 123-4567"
                />

              {/* <input
                name="home_phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                className={`form-control rounded-3 shadow-sm ${getFieldError('home_phone') ? 'is-invalid' : ''}`}
                value={formik.values.home_phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              /> */}
              <div className="invalid-feedback d-block">{getFieldError('home_phone')}</div>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">Mobile Phone</label>

                <CustomPhoneInput
                formik={formik}
                name="mobile_phone"
                type="tel"
                placeholder="+1 (555) 987-6543"
                />

              {/* <input
                name="mobile_phone"
                type="tel"
                placeholder="+1 (555) 987-6543"
                className={`form-control rounded-3 shadow-sm ${getFieldError('mobile_phone') ? 'is-invalid' : ''}`}
                value={formik.values.mobile_phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              /> */}
              <div className="invalid-feedback d-block">{getFieldError('mobile_phone')}</div>
            </div>
          </div>
        </div>

        {/* Professional Information Section */}
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">Professional Information</h6>
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">NPI Number</label>
              <input
                name="npi_number"
                placeholder="1234567890"
                className={`form-control rounded-3 shadow-sm ${getFieldError('npi_number') ? 'is-invalid' : ''}`}
                value={formik.values.npi_number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                maxLength="10"
              />
              <div className="invalid-feedback d-block">{getFieldError('npi_number')}</div>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">NPI Enumeration Date</label>
              <CustomDatePicker
                name="npi_enum_date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('npi_enum_date') ? 'is-invalid' : ''}`}
                value={formik.values.npi_enum_date}
                formik={formik}
              />
              {/* <input
                name="npi_enum_date"
                type="date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('npi_enum_date') ? 'is-invalid' : ''}`}
                value={formik.values.npi_enum_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              /> */}
              <div className="invalid-feedback d-block">{getFieldError('npi_enum_date')}</div>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">NPI Social Security</label>
              <input
                name="npi_social_security"
                placeholder="Enter social security"
                className={`form-control rounded-3 shadow-sm ${getFieldError('npi_social_security') ? 'is-invalid' : ''}`}
                value={formik.values.npi_social_security}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="invalid-feedback d-block">{getFieldError('npi_social_security')}</div>
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12 col-md-6">
              <SearchableDropdown
                label="Telehealth Experience (Years)"
                placeholder="Select Option"
                options={providerSettings.telemed_exp}
                value={formik.values.telemed_exp ?? ''}
                onChange={(value) => formik.setFieldValue('telemed_exp', parseNumberOrNull(value))}
                disabled={settingsLoading}
                loading={settingsLoading}
                className={getFieldError('telemed_exp') ? 'is-invalid' : ''}
                emptyMessage="No telehealth experience options found"
              />
              <div className="invalid-feedback d-block">{getFieldError('telemed_exp')}</div>
            </div>
          </div>
        </div>

        {/* License Information Section */}
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">License Information</h6>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">License ID</label>
              <input
                name="license_id"
                placeholder="Enter license ID"
                className={`form-control rounded-3 shadow-sm ${getFieldError('license_id') ? 'is-invalid' : ''}`}
                value={formik.values.license_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="invalid-feedback d-block">{getFieldError('license_id')}</div>
            </div>

            <div className="col-12 col-md-6">
              <SearchableDropdown
                label="License State"
                placeholder="Select Option"
                options={licenseStateOptions}
                value={formik.values.license_state || ''}
                onChange={(value) => formik.setFieldValue('license_state', value || '')}
                disabled={settingsLoading && !licenseStateOptions.length}
                loading={settingsLoading && !licenseStateOptions.length}
                className={getFieldError('license_state') ? 'is-invalid' : ''}
                emptyMessage="No license states found"
                allowCustom={true}
              />
              <div className="invalid-feedback d-block">{getFieldError('license_state')}</div>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">License Issue Date</label>
              {/* <input
                name="license_issue_date"
                type="date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('license_issue_date') ? 'is-invalid' : ''}`}
                value={formik.values.license_issue_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              /> */}


              <CustomDatePicker
                name="license_issue_date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('license_issue_date') ? 'is-invalid' : ''}`}
                value={formik.values.license_issue_date}
                formik={formik}
              />
              <div className="invalid-feedback d-block">{getFieldError('license_issue_date')}</div>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">License Expiration Date</label>
              {/* <input
                name="license_exp_date"
                type="date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('license_exp_date') ? 'is-invalid' : ''}`}
                value={formik.values.license_exp_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              /> */}

                 <CustomDatePicker
                name="license_exp_date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('license_exp_date') ? 'is-invalid' : ''}`}
                value={formik.values.license_exp_date}
                formik={formik}
              />

              <div className="invalid-feedback d-block">{getFieldError('license_exp_date')}</div>
            </div>
          </div>
        </div>

        {/* CAQH Information Section */}
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">CAQH Information</h6>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">CAQH ID</label>
              <input
                name="caqh_id"
                placeholder="Enter CAQH ID"
                className={`form-control rounded-3 shadow-sm ${getFieldError('caqh_id') ? 'is-invalid' : ''}`}
                value={formik.values.caqh_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <div className="invalid-feedback d-block">{getFieldError('caqh_id')}</div>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">CAQH Issue Date</label>
              <CustomDatePicker
                name="caqh_issue_date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('caqh_issue_date') ? 'is-invalid' : ''}`}
                value={formik.values.caqh_issue_date}
                formik={formik}
              />
        
              <div className="invalid-feedback d-block">{getFieldError('caqh_issue_date')}</div>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">CAQH Attention Date</label>
              <CustomDatePicker
                name="caqh_attention_date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('caqh_attention_date') ? 'is-invalid' : ''}`}
                value={formik.values.caqh_attention_date}
                formik={formik}
              />

              <div className="invalid-feedback d-block">{getFieldError('caqh_attention_date')}</div>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label fw-semibold">CAQH Reattestation Date</label>
              <CustomDatePicker
                name="caqh_reattestation_date"
                className={`form-control rounded-3 shadow-sm ${getFieldError('caqh_reattestation_date') ? 'is-invalid' : ''}`}
                value={formik.values.caqh_reattestation_date}
                formik={formik}
              />

              <div className="invalid-feedback d-block">{getFieldError('caqh_reattestation_date')}</div>
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12 col-md-6">
              <div className="form-check">
                <input
                  name="caqh_enabled"
                  type="checkbox"
                  className="form-check-input"
                  id="caqhEnabled"
                  checked={formik.values.caqh_enabled}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <label className="form-check-label fw-semibold" htmlFor="caqhEnabled">
                  CAQH Enabled
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="p-4 rounded-4 border bg-light-subtle mb-4">
          <h6 className="fw-bold hc-text-primary mb-3">Home Address Information</h6>
          <AddressSelectorFields
            title="Current Address"
            fieldPrefix="address"
            values={formik.values.address}
            errors={formik.errors.address || {}}
            touched={formik.touched.address || {}}
            onFieldChange={(fieldName, value) => formik.setFieldValue(fieldName, value)}
            renderAsterisk={renderAsterisk}
          />
        </div>

        {/* Birth Address Information Section */}
        <div className="p-4 rounded-4 border bg-light-subtle mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h6 className="fw-bold hc-text-primary m-0">Birth Address Information</h6>
            <div className="form-check m-0">
              <input
                name="is_authorized_to_work"
                type="checkbox"
                className="form-check-input"
                id="authorizedToWork"
                checked={formik.values.is_authorized_to_work}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <label className="form-check-label small text-muted" htmlFor="authorizedToWork">
                Authorized to Work
              </label>
            </div>
          </div>
          <AddressSelectorFields
            title="Birth Address"
            fieldPrefix="birth_address"
            values={formik.values.birth_address}
            errors={formik.errors.birth_address || {}}
            touched={formik.touched.birth_address || {}}
            onFieldChange={(fieldName, value) => formik.setFieldValue(fieldName, value)}
            renderAsterisk={renderAsterisk}
          />
        </div>

        {/* Primary Location Address Information Section */}
        <div className="p-4 rounded-4 border bg-light-subtle mb-4">
          <h6 className="fw-bold hc-text-primary mb-3">Primary Location Address</h6>
          <AddressSelectorFields
            title="Primary Location Address"
            fieldPrefix="primary_location_address"
            values={formik.values.primary_location_address}
            errors={formik.errors.primary_location_address || {}}
            touched={formik.touched.primary_location_address || {}}
            onFieldChange={(fieldName, value) => formik.setFieldValue(fieldName, value)}
            renderAsterisk={renderAsterisk}
          />
        </div>

        {/* Secondary Location Address Information Section */}
        <div className="p-4 rounded-4 border bg-light-subtle mb-4">
          <h6 className="fw-bold hc-text-primary mb-3">Secondary Location Address</h6>
          <AddressSelectorFields
            title="Secondary Location Address"
            fieldPrefix="secondary_location_address"
            values={formik.values.secondary_location_address}
            errors={formik.errors.secondary_location_address || {}}
            touched={formik.touched.secondary_location_address || {}}
            onFieldChange={(fieldName, value) => formik.setFieldValue(fieldName, value)}
            renderAsterisk={renderAsterisk}
          />
        </div>

        {/* Organization & Status Section */}
        <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
          <h6 className="fw-bold hc-text-primary mb-3">Assignment & Status</h6>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <SearchableDropdown
                label="Organization"
                placeholder="Select Option"
                options={(Array.isArray(organizations) ? organizations : []).map((org) => ({
                  value: org.id ?? org.OrganizationId ?? org.organization_id ?? org.Id ?? org.value ?? '',
                  label:
                    org.group_office_name ??
                    org.OrganizationName ??
                    org.organization_name ??
                    org.name ??
                    org.label ??
                    'Unknown Organization',
                }))}
                value={formik.values.organization_id}
                onChange={(value) => formik.setFieldValue('organization_id', value || '')}
                disabled={orgLoading}
                loading={orgLoading}
                required
                emptyMessage="No organizations found"
              />
              <div className="invalid-feedback d-block">{getFieldError('organization_id')}</div>
            </div>

            <div className="col-12 col-md-6">
              <SearchableDropdown
                label="Status"
                placeholder="Select Option"
                options={providerSettings.status}
                value={formik.values.status_id ?? ''}
                onChange={(value) => formik.setFieldValue('status_id', parseNumberOrNull(value))}
                disabled={settingsLoading}
                loading={settingsLoading}
                required
                className={getFieldError('status_id') ? 'is-invalid' : ''}
                emptyMessage="No statuses found"
              />
              <div className="invalid-feedback d-block">{getFieldError('status_id')}</div>
            </div>
          </div>
        </div>
        <div className="text-end mt-4">
          <button
            type="submit"
            className="btn hc-bg-primary text-white px-5 py-2 rounded-3 fw-semibold shadow"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Saving...
              </>
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
